"use client";

const DB_NAME = "te-search";
const DB_VERSION = 2;
const STORE = "attempt-index";
const STORE_STUDENTS = "student-index";

export interface ExamAttempts {
  assignmentId: number;
  assignmentName: string | null;
  status: string | null;
  at: number;
  minId: number;
  maxId: number;
  partial?: boolean;
  entries: Array<[number, number]>;
}

export interface StudentCard {
  studentId: number;
  name: string | null;
  photo: string | null;
  at: number;
  hit?: unknown;
}

export interface StudentSession {
  assignmentId: number;
  assignmentName: string | null;
  status: string | null;
  attemptId: number;
}

interface MemoryIndex {
  byAttempt: Map<number, { assignmentId: number; studentId: number }>;
  byExam: Map<number, ExamAttempts>;
  students: Map<number, StudentCard>;
  byUser: Map<number, number>;
}

let memory: MemoryIndex | null = null;
let loading: Promise<MemoryIndex> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (typeof window === "undefined" || !("indexedDB" in window)) return Promise.resolve(null);

  return new Promise((resolve) => {
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "assignmentId" });
        }
        if (!db.objectStoreNames.contains(STORE_STUDENTS)) {
          db.createObjectStore(STORE_STUDENTS, { keyPath: "studentId" });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function readAll<T>(db: IDBDatabase, store: string): Promise<T[]> {
  return new Promise((resolve) => {
    try {
      const request = db.transaction(store, "readonly").objectStore(store).getAll();

      request.onsuccess = () => resolve((request.result as T[]) ?? []);
      request.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

function put(db: IDBDatabase, store: string, record: unknown): Promise<void> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(store, "readwrite");

      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
      tx.objectStore(store).put(record);
    } catch {
      resolve();
    }
  });
}

async function load(): Promise<MemoryIndex> {
  if (memory) return memory;
  if (loading) return loading;

  loading = (async () => {
    const index: MemoryIndex = {
      byAttempt: new Map(),
      byExam: new Map(),
      students: new Map(),
      byUser: new Map(),
    };
    const db = await openDb();

    if (db) {
      for (const exam of await readAll<ExamAttempts>(db, STORE)) {
        index.byExam.set(exam.assignmentId, exam);

        for (const [attemptId, studentId] of exam.entries ?? []) {
          index.byAttempt.set(attemptId, { assignmentId: exam.assignmentId, studentId });
        }
      }

      for (const student of await readAll<StudentCard>(db, STORE_STUDENTS)) {
        index.students.set(student.studentId, student);

        const userId = Number((student.hit as { userId?: unknown } | undefined)?.userId);
        if (Number.isFinite(userId) && userId > 0) index.byUser.set(userId, student.studentId);
      }

      db.close();
    }

    memory = index;
    return index;
  })();

  return loading;
}

export async function lookupAttempt(attemptId: number): Promise<{
  assignmentId: number;
  assignmentName: string | null;
  status: string | null;
  studentId: number;
  studentName: string | null;
  statusAt: number;
} | null> {
  const index = await load();
  const hit = index.byAttempt.get(attemptId);

  if (!hit) return null;

  const exam = index.byExam.get(hit.assignmentId);

  return {
    assignmentId: hit.assignmentId,
    assignmentName: exam?.assignmentName ?? null,
    status: exam?.status ?? null,
    studentId: hit.studentId,
    studentName: index.students.get(hit.studentId)?.name ?? null,
    statusAt: exam?.at ?? 0,
  };
}

export async function lookupStudent(studentId: number): Promise<StudentCard | null> {
  const index = await load();
  return index.students.get(studentId) ?? null;
}

export async function lookupStudentSessions(
  studentId: number,
  exceptAttemptId?: number
): Promise<StudentSession[]> {
  const index = await load();
  const sessions: StudentSession[] = [];

  for (const exam of index.byExam.values()) {
    for (const [attemptId, owner] of exam.entries ?? []) {
      if (owner !== studentId || attemptId === exceptAttemptId) continue;

      sessions.push({
        assignmentId: exam.assignmentId,
        assignmentName: exam.assignmentName,
        status: exam.status,
        attemptId,
      });
    }
  }

  return sessions.sort((a, b) => b.attemptId - a.attemptId);
}

export async function canSkipExam(assignmentId: number, attemptId: number): Promise<boolean> {
  const index = await load();
  const exam = index.byExam.get(assignmentId);

  if (!exam || exam.partial) return false;

  return exam.maxId >= attemptId && !index.byAttempt.has(attemptId);
}

export async function rememberExam(
  assignmentId: number,
  assignmentName: string | null,
  status: string | null,
  attempts: Array<{ id: number | string; student_id: number | string }>,
  partial = false
): Promise<void> {
  const index = await load();
  const previous = index.byExam.get(assignmentId);

  const merged = new Map<number, number>(partial ? previous?.entries ?? [] : []);
  let maxId = partial ? previous?.maxId ?? 0 : 0;
  let minId = partial ? previous?.minId ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;

  for (const attempt of attempts) {
    const id = Number(attempt.id);
    const studentId = Number(attempt.student_id);

    if (!Number.isFinite(id) || !Number.isFinite(studentId)) continue;

    merged.set(id, studentId);
    index.byAttempt.set(id, { assignmentId, studentId });
    if (id > maxId) maxId = id;
    if (id < minId) minId = id;
  }

  const record: ExamAttempts = {
    assignmentId,
    assignmentName: assignmentName ?? previous?.assignmentName ?? null,
    status: status ?? previous?.status ?? null,
    at: Date.now(),
    minId: minId === Number.MAX_SAFE_INTEGER ? 0 : minId,
    maxId,
    partial: partial && (previous?.partial ?? true),
    entries: [...merged.entries()],
  };

  index.byExam.set(assignmentId, record);

  const db = await openDb();
  if (!db) return;

  await put(db, STORE, record);
  db.close();
}

const pendingStudents = new Map<number, StudentCard>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

async function flushStudents(): Promise<void> {
  flushTimer = null;

  const batch = [...pendingStudents.values()];
  pendingStudents.clear();

  if (batch.length === 0) return;

  const db = await openDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_STUDENTS, "readwrite");
      const store = tx.objectStore(STORE_STUDENTS);

      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();

      for (const record of batch) store.put(record);
    } catch {
      resolve();
    }
  });

  db.close();
}

export async function rememberStudent(
  studentId: number,
  name: string | null,
  photo: string | null,
  hit?: unknown
): Promise<void> {
  if (!Number.isFinite(studentId) || studentId <= 0) return;

  const index = await load();
  const previous = index.students.get(studentId);

  const record: StudentCard = {
    studentId,
    name: name ?? previous?.name ?? null,
    photo: photo ?? previous?.photo ?? null,
    at: Date.now(),
    hit: hit ?? previous?.hit,
  };

  const sameName = previous?.name === record.name && previous?.photo === record.photo;
  const sameHit = JSON.stringify(previous?.hit ?? null) === JSON.stringify(record.hit ?? null);

  if (previous && sameName && sameHit) return;

  index.students.set(studentId, record);
  pendingStudents.set(studentId, record);

  const userId = Number((record.hit as { userId?: unknown } | undefined)?.userId);
  if (Number.isFinite(userId) && userId > 0) index.byUser.set(userId, studentId);

  if (!flushTimer) flushTimer = setTimeout(() => void flushStudents(), 400);
}

export interface ExamRange {
  minId: number;
  maxId: number;
  complete: boolean;
}

export async function examRanges(assignmentIds: number[]): Promise<Map<number, ExamRange>> {
  const index = await load();
  const ranges = new Map<number, ExamRange>();

  for (const assignmentId of assignmentIds) {
    const exam = index.byExam.get(assignmentId);
    if (!exam || !exam.maxId) continue;

    ranges.set(assignmentId, {
      minId: exam.minId || exam.maxId,
      maxId: exam.maxId,
      complete: !exam.partial,
    });
  }

  return ranges;
}

export async function lookupStudentsByUserIds(
  userIds: number[],
  freshMs: number
): Promise<{ hits: unknown[]; missing: number[] }> {
  const index = await load();
  const hits: unknown[] = [];
  const missing: number[] = [];
  const now = Date.now();

  for (const userId of userIds) {
    const studentId = index.byUser.get(userId);
    const card = studentId != null ? index.students.get(studentId) : undefined;

    if (card?.hit && now - card.at < freshMs) hits.push(card.hit);
    else missing.push(userId);
  }

  return { hits, missing };
}

export async function dropAttemptIndex(): Promise<void> {
  memory = null;
  loading = null;

  const db = await openDb();
  if (!db) return;

  try {
    const tx = db.transaction([STORE, STORE_STUDENTS], "readwrite");
    tx.objectStore(STORE).clear();
    tx.objectStore(STORE_STUDENTS).clear();
  } catch {
    return;
  } finally {
    db.close();
  }
}
